import { forEach, isEqual, isNil, mapValues, merge, pickBy } from 'lodash-es';
import { Nil } from '../definition';

export function objSanitizeNilValues<T = Record<string, any>>(
  obj?: Record<string, any>
): T {
  return pickBy(obj, (v) => !isNil(v)) as T;
}

export function isPrimitiveType(val: any) {
  return !(typeof val === 'function' || (typeof val === 'object' && val));
}

export function objectDiff<T>(
  objectA?: Nil<Partial<T> | Record<string, any>>,
  objectB?: Nil<Partial<T> | Record<string, any>>
) {
  objectB ||= {};
  objectA = merge(
    mapValues(objectB, () => null),
    objectA
  );
  const diff: Record<string, any> = {};

  forEach(objectA, (value, key) => {
    if (!isEqual(value, (objectB as any)[key])) {
      diff[key] = (objectB as any)[key];
    }
  });

  return diff as Partial<T>;
}

export function deepClone<T>(obj: T): T {
  if (isPrimitiveType(obj)) return obj;

  const cloneObj = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      (cloneObj as any)[key] = deepClone(obj[key]);
    }
  }

  return cloneObj as T;
}

export function keepOnlyProperty<T extends Record<string, any>>(
  obj: T,
  keyToKeep: keyof T
): Partial<T> {
  return keyToKeep in obj
    ? ({ [keyToKeep]: obj[keyToKeep] } as Partial<T>)
    : {};
}

export function stableHash(obj: any): string {
  const seen = new WeakSet();

  const stringify = (item: any): string => {
    if (item === null) return 'null';
    if (typeof item !== 'object') return String(item);
    if (seen.has(item)) return '[Circular]';
    seen.add(item);

    const keys = Object.keys(item).sort();
    return `{${keys.map((key) => `${key}:${stringify(item[key])}`).join(',')}}`;
  };

  return stringify(obj);
}
